import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/slices/authSlice";
import { useGetVisitorsQuery } from "../redux/services/api";
import Icon from "../components/Icon";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";

const VisitorDetails = () => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isArabic } = useLanguage();
  const [searchParams] = useSearchParams();

  const [filterType, setFilterType] = useState(
    searchParams.get("type") || "today"
  );
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Use RTK Query for data fetching
  const {
    data: visitorsResponse,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useGetVisitorsQuery({
    type: filterType,
    page: currentPage,
    per_page: itemsPerPage,
  });

  const visitors = visitorsResponse?.data?.visitors || [];
  const totalPages = visitorsResponse?.data?.total_pages || 1;
  const totalVisitors = visitorsResponse?.data?.total_visitors || 0;
  const error = queryError?.data?.message || queryError?.message;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(isArabic ? "ar-SA" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getLocationFromIP = (visitor) => {
    // Use the country data from the backend if available
    if (visitor.country && visitor.country !== "Unknown") {
      return visitor.country;
    }

    // Handle local/private IPs
    if (
      visitor.country === "Local" ||
      visitor.visitor_ip === "127.0.0.1" ||
      visitor.visitor_ip === "::1"
    ) {
      return t("visitorDetails.local");
    }

    return t("visitorDetails.unknownCountry");
  };

  const getBrowserInfo = (userAgent) => {
    if (!userAgent) return t("visitorDetails.unknown");

    if (userAgent.includes("Chrome")) return "Chrome";
    if (userAgent.includes("Firefox")) return "Firefox";
    if (userAgent.includes("Safari")) return "Safari";
    if (userAgent.includes("Edge")) return "Edge";
    return t("visitorDetails.other");
  };

  const getDeviceInfo = (userAgent) => {
    if (!userAgent) return t("visitorDetails.unknown");

    if (userAgent.includes("Mobile")) return t("visitorDetails.mobile");
    if (userAgent.includes("Tablet")) return t("visitorDetails.tablet");
    return t("visitorDetails.desktop");
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (newFilter) => {
    setFilterType(newFilter);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="visitor-details-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("visitorDetails.loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="visitor-details-page">
        <div className="error-container">
          <Icon name="exclamation-triangle" size="48px" />
          <h3>{t("visitorDetails.error")}</h3>
          <p>{error}</p>
          <button onClick={() => refetch()} className="retry-btn">
            {t("visitorDetails.retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="visitor-details-page">
      {/* Header */}
      <div className="visitor-details-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate("/dashboard")}>
            <Icon name="arrowLeft" size="16px" />
            {t("visitorDetails.backToDashboard")}
          </button>
          <div className="header-title">
            <h1>{t("visitorDetails.visitorDetails")}</h1>
            <p>{t("visitorDetails.visitorAnalytics")}</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="refresh-btn"
            onClick={() => refetch()}
            disabled={loading}
          >
            <Icon name="refresh" className="btn-icon" size="16px" />
            {t("visitorDetails.refresh")}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="visitor-filters">
        <div className="filter-group">
          <label>{t("visitorDetails.period")}:</label>
          <select
            value={filterType}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="filter-select"
          >
            <option value="today">{t("visitorDetails.today")}</option>
            <option value="week">{t("visitorDetails.thisWeek")}</option>
            <option value="month">{t("visitorDetails.thisMonth")}</option>
            <option value="all">{t("visitorDetails.allTime")}</option>
          </select>
        </div>
        <div className="visitor-stats">
          <span className="total-visitors">
            {t("visitorDetails.totalVisitors")}:{" "}
            <strong>{totalVisitors}</strong>
          </span>
        </div>
      </div>

      {/* Visitors Table */}
      <div className="visitor-details-content">
        <div className="visitor-table-container">
          <table className="visitor-table">
            <thead>
              <tr>
                <th>{t("visitorDetails.visitTime")}</th>
                <th>{t("visitorDetails.page")}</th>
                <th>{t("visitorDetails.pageType")}</th>
                <th>{t("visitorDetails.ipAddress")}</th>
                <th>{t("visitorDetails.country")}</th>
                <th>{t("visitorDetails.browser")}</th>
                <th>{t("visitorDetails.device")}</th>
                <th>{t("visitorDetails.referrer")}</th>
              </tr>
            </thead>
            <tbody>
              {visitors.length > 0 ? (
                visitors.map((visitor, index) => (
                  <tr key={index}>
                    <td className="visit-time">
                      {formatDate(visitor.visited_at)}
                    </td>
                    <td className="page-info">
                      <div className="page-title">
                        {visitor.page_title || visitor.page_path}
                      </div>
                      <div className="page-path">{visitor.page_path}</div>
                    </td>
                    <td>
                      <span className={`page-type-badge ${visitor.page_type}`}>
                        {visitor.page_type}
                      </span>
                    </td>
                    <td className="ip-address">{visitor.visitor_ip}</td>
                    <td className="country">
                      <div className="country-info">
                        <span className="country-name">
                          {getLocationFromIP(visitor)}
                        </span>
                        {visitor.country_code &&
                          visitor.country_code !== "UN" && (
                            <span className="country-code">
                              ({visitor.country_code})
                            </span>
                          )}
                      </div>
                    </td>
                    <td className="browser">
                      {getBrowserInfo(visitor.user_agent)}
                    </td>
                    <td className="device">
                      {getDeviceInfo(visitor.user_agent)}
                    </td>
                    <td className="referrer">
                      {visitor.referer ? (
                        <a
                          href={visitor.referer}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="referrer-link"
                        >
                          {visitor.referer.length > 30
                            ? visitor.referer.substring(0, 30) + "..."
                            : visitor.referer}
                        </a>
                      ) : (
                        <span className="no-referrer">
                          {t("visitorDetails.direct")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="no-data">
                    {t("visitorDetails.noVisitorsFound")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <Icon name="arrowLeft" size="16px" />
              {t("visitorDetails.previous")}
            </button>

            <div className="pagination-info">
              {t("visitorDetails.page")} {currentPage} {t("visitorDetails.of")}{" "}
              {totalPages}
            </div>

            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              {t("visitorDetails.next")}
              <Icon name="arrowRight" size="16px" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisitorDetails;
